import axios, {AxiosError} from 'axios'
import {apiURL} from '_metronic/helpers'
import type {
  AiChatRequest,
  AiChatResponse,
  AiHealthResponse,
  AiSetupExplainResponse,
} from './_types'

// LLM responses sometimes take 30–60s. We allow up to 2 minutes before axios
// gives up — long enough for slow upstream LLMs but bounded so the user gets
// a clear failure instead of a hung "Thinking…" forever. Any intermediate
// proxy (CloudFront, ALB, Cloudflare) must be configured to allow at least
// this long; otherwise the user will see a 504 surfaced via our retry logic.
export const AI_REQUEST_TIMEOUT_MS = 120_000

// At most this many extra attempts after the original. 2 retries = 3 total.
export const AI_MAX_RETRIES = 2

// HTTP statuses that almost always indicate a transient issue worth retrying.
// 408 Request Timeout, 425 Too Early, 429 Too Many Requests, 5xx server errors.
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504])

const AI_AXIOS_CONFIG = {timeout: AI_REQUEST_TIMEOUT_MS}

export async function postAiChat(payload: AiChatRequest): Promise<AiChatResponse> {
  const {data} = await axios.post<AiChatResponse>(apiURL('/ai/chat'), payload, AI_AXIOS_CONFIG)
  return data
}

export async function explainSetup(setupId: string): Promise<AiSetupExplainResponse> {
  const {data} = await axios.post<AiSetupExplainResponse>(
    apiURL('/ai/setup', setupId, 'explain'),
    {},
    AI_AXIOS_CONFIG
  )
  return data
}

export async function getAiHealth(): Promise<AiHealthResponse> {
  const {data} = await axios.get<AiHealthResponse>(apiURL('/ai/health'), AI_AXIOS_CONFIG)
  return data
}

/**
 * react-query `retry` predicate for AI mutations / queries.
 *
 * Returns true if the failure is one we can reasonably hope will resolve on a
 * second attempt: a fast network error (DNS, dropped connection, transient
 * 5xx, rate limit). Returns false for:
 *  - Auth / authorization / not-found failures (4xx) — the user has to fix
 *    the situation, retrying just wastes their time.
 *  - Axios timeouts (`ECONNABORTED`) — we already waited the full window;
 *    looping costs the user another 2 minutes. They can press Retry manually
 *    if they choose to.
 *  - Anything past `AI_MAX_RETRIES`.
 */
export function shouldRetryAi(failureCount: number, error: unknown): boolean {
  if (failureCount >= AI_MAX_RETRIES) return false
  if (!axios.isAxiosError(error)) return false

  const axErr = error as AxiosError

  // Don't auto-retry timeouts — surface them so user can decide.
  if (axErr.code === 'ECONNABORTED') return false

  // Don't retry cancelled requests.
  if (axios.isCancel?.(axErr) || axErr.code === 'ERR_CANCELED') return false

  const status = axErr.response?.status

  // No response: the request never came back (DNS, network drop, CORS preflight
  // failure, server-killed connection). These are usually transient — try again.
  if (status === undefined) return true

  return RETRYABLE_STATUSES.has(status)
}

/**
 * Exponential backoff with jitter, capped at 5s. Attempt index is 0-based.
 *   attempt 0  -> ~1s
 *   attempt 1  -> ~2s
 *   attempt 2+ -> ~4s (capped)
 */
export function aiRetryDelay(attemptIndex: number): number {
  const base = Math.min(1000 * 2 ** attemptIndex, 5000)
  // Up to 30% jitter so simultaneous failures don't all hammer the server at the same instant.
  const jitter = Math.random() * 0.3 * base
  return Math.round(base + jitter)
}

/**
 * Turn an arbitrary error into a short, actionable message that we can show
 * directly in the AI chat UI. We try the API's own error body first (it
 * usually has the friendliest wording), then fall back to category-based
 * messages so the user always knows whether the problem is on their side
 * (auth / connection) or ours (upstream LLM).
 */
export function extractAxiosErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as any
    const apiMsg =
      data?.message ||
      data?.detail ||
      (Array.isArray(data?.messages) ? data.messages[0] : undefined)
    if (typeof apiMsg === 'string' && apiMsg.trim().length > 0) return apiMsg

    // No useful body — categorize the failure so the user knows what to do.
    const msg = (error.message || '').toLowerCase()

    if (error.code === 'ECONNABORTED' || msg.includes('timeout')) {
      return 'The AI assistant took too long to respond. Please try again.'
    }
    if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') {
      return 'Request was cancelled before it finished.'
    }
    if (!error.response) {
      // No response object reached axios. Two common causes:
      //   1. True network failure (rare — the rest of the app obviously works
      //      if the user got far enough to send a chat message).
      //   2. The server returned a 5xx whose CORS headers were stripped by an
      //      intermediate proxy, so the browser hides the response from JS.
      // Either way the prompt itself is fine — surface this as a backend hiccup
      // so the user knows to just press Retry instead of debugging their wifi.
      return 'AI assistant did not respond. This is usually a temporary backend hiccup — please try again.'
    }

    const status = error.response.status
    if (status === 401) return 'Your session has expired. Please log in again.'
    if (status === 403) return 'You do not have access to the AI assistant.'
    if (status === 404) return 'AI endpoint not found. Please contact support if this persists.'
    if (status === 408) return 'The AI assistant took too long to respond. Please try again.'
    if (status === 429) {
      return 'Too many requests right now — please wait a moment and try again.'
    }
    if (status >= 500 && status < 600) {
      return 'AI service is having a hiccup. Please try again in a moment.'
    }

    return error.message || fallback
  }

  if (error instanceof Error) return error.message || fallback
  return fallback
}
